<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useUser } from '@/composables/useUser'
import { useMetricSummaryStore } from '@/store/metricSummaryStore'
import { UserRoles } from '@/shared/models/UserRoles'
import MetricsBadge from './MetricsBadge.vue'
import MetricsFlyoutDrawer from './MetricsFlyoutDrawer.vue'

const STORAGE_KEY_PREFIX = 'dev-metrics-panel-state'

const { user } = useUser()
const store = useMetricSummaryStore()

// Role gating
const isPlatformDeveloper = computed(() => {
  return !!user.roles?.includes(UserRoles.PlatformDeveloper)
})

// Panel open/closed state
const isOpen = ref(false)

// Computed metrics from store
const inFlightCount = computed(() => {
  const parsed = parseInt(store.data.user.inFlight, 10)
  return isNaN(parsed) ? 0 : parsed
})

const isActive = computed(() => inFlightCount.value > 0)

const isLoading = computed(() => store.data.loading)

const hasError = computed(() => {
  // The store doesn't expose a dedicated error field; treat loading: false
  // with empty userId and lastFetch === 0 as a potential error state.
  // However, based on the store implementation, there's no explicit error state.
  // We'll check if data has been fetched at least once but has no user id
  // while not loading — this indicates a fetch failure scenario.
  return !store.data.loading && store.data.lastFetch === 0 && store.data.userId === ''
})

// localStorage helpers
function getStorageKey(): string {
  return `${STORAGE_KEY_PREFIX}:${user.id}`
}

function readPersistedState(): boolean {
  try {
    const key = getStorageKey()
    const raw = localStorage.getItem(key)
    if (raw === null) return false
    const parsed = JSON.parse(raw)
    if (typeof parsed?.isOpen === 'boolean') {
      return parsed.isOpen
    }
    return false
  } catch {
    return false
  }
}

function persistState(open: boolean): void {
  try {
    const key = getStorageKey()
    localStorage.setItem(key, JSON.stringify({ isOpen: open }))
  } catch {
    // Graceful degradation — localStorage unavailable (e.g., private browsing)
  }
}

// Restore state on mount
onMounted(() => {
  if (isPlatformDeveloper.value) {
    isOpen.value = readPersistedState()
  }
})

// Event handlers
function handleOpen() {
  isOpen.value = true
  persistState(true)
}

function handleClose() {
  isOpen.value = false
  persistState(false)
}
</script>

<template>
  <div v-if="isPlatformDeveloper" class="developer-metrics-panel">
    <MetricsFlyoutDrawer
      :open="isOpen"
      :metrics="store.data"
      :is-loading="isLoading"
      :has-error="hasError"
      @close="handleClose"
    />
    <MetricsBadge
      v-if="!isOpen"
      :in-flight-count="inFlightCount"
      :is-active="isActive"
      :is-loading="isLoading"
      @open="handleOpen"
    />
  </div>
</template>

<style scoped>
.developer-metrics-panel {
  /* No layout impact — children use fixed positioning */
}
</style>
